<template>
  <div id="app">
    <router-view :key="route.name + '-' + route.params.id" />
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const route = useRoute()
const { t } = useI18n({ useScope: 'global' })

const BASE_URL = 'https://gmgautomation.net'
const OG_IMAGE = `${BASE_URL}/og-image.jpg`
const SUPPORTED_LOCALES = ['en', 'it', 'fr', 'de', 'es', 'pt', 'uk']

useHead(
  computed(() => {
    const locale = route.params.locale || 'en'

    const title = route.meta.titleKey
      ? t(route.meta.titleKey)
      : (route.meta.title || 'GMG Automation')

    const description = route.meta.descriptionKey
      ? t(route.meta.descriptionKey)
      : ''

    const canonical = `${BASE_URL}${route.path}`

    // Replace the locale segment at the start of the path to build alternate URLs
    const pathWithoutLocale = route.path.replace(/^\/[^/]+/, '')

    const hreflangLinks = SUPPORTED_LOCALES.map((lang) => ({
      rel: 'alternate',
      hreflang: lang,
      href: `${BASE_URL}/${lang}${pathWithoutLocale}`,
    }))
    hreflangLinks.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${BASE_URL}/en${pathWithoutLocale}`,
    })

    const meta = [
      { property: 'og:site_name', content: 'GMG Automation' },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: title },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: OG_IMAGE },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:image', content: OG_IMAGE },
    ]

    if (description) {
      meta.push({ name: 'description', content: description })
      meta.push({ property: 'og:description', content: description })
      meta.push({ name: 'twitter:description', content: description })
    }

    if (route.meta.noindex) {
      meta.push({ name: 'robots', content: 'noindex, nofollow' })
    }

    return {
      htmlAttrs: { lang: locale },
      title,
      meta,
      link: [{ rel: 'canonical', href: canonical }, ...hreflangLinks],
    }
  })
)
</script>
